import { useMemo } from 'react';

import type { Suggestion } from '@atlassian/generative-ai-modal/screens/UserInputCommandPalette';

import {
	TELL_AI,
	TELL_AGENT,
	USE_GENERAL_AI_KNOWLEDGE,
	USE_ROVO_ORGANISATION_KNOWLEDGE,
} from '../utils/constants';
import { useAIExperienceCommonDataContext } from './useAIExperienceCommonData';

/**
 * The use page knowledge toggle should only be visible when:
 * - there are agent conversation starters
 * - the remaining suggestions allow toggling page knowledge, unless a nested menu is selected
 */
export const usePageKnowledgeToggle = ({
	suggestions,
	agentVisible,
	nestedMenuSelected,
}: {
	agentVisible: boolean;
	nestedMenuSelected: boolean;
	suggestions: Suggestion[];
}): {
	isEnabled: boolean;
	isVisible: boolean;
	onToggle: () => void;
} => {
	const { sendToAIExperienceMachine, includeKnowledgeFromCurrentPage } =
		useAIExperienceCommonDataContext();

	const pageKnowledgeIsVisible = useMemo(() => {
		const isGeneralAISuggestionRemaining = [
			TELL_AI,
			TELL_AGENT,
			USE_GENERAL_AI_KNOWLEDGE,
			USE_ROVO_ORGANISATION_KNOWLEDGE,
		].includes(suggestions[0]?.value);

		return !!(agentVisible || (isGeneralAISuggestionRemaining && !nestedMenuSelected));
	}, [agentVisible, nestedMenuSelected, suggestions]);

	const pageKnowledgeProps = useMemo(() => {
		return {
			isEnabled: !!includeKnowledgeFromCurrentPage,
			isVisible: pageKnowledgeIsVisible,
			onToggle: () => sendToAIExperienceMachine({ type: 'toggle use page knowledge' }),
		};
	}, [includeKnowledgeFromCurrentPage, pageKnowledgeIsVisible, sendToAIExperienceMachine]);

	return pageKnowledgeProps;
};
